/**
 * PolarstarIntro.jsx — Host introduction page for Polarstar Kids.
 *
 * §HOST-INTRO 2026-02 — Polarstar keeper of wonder (bedtime light
 * for children, parent-guided).
 *
 * Polarstar's symbol system: star · moon · soft night sky · quiet
 * bedtime ritual. Feeling: safety after dark, one light still on.
 *
 * Uses room_polarstar_sample_v1.png — a warm, painterly night
 * scene of a child's window with a single bright star overhead.
 *
 * Polarstar's room route is /kids-universe/polarstar (the
 * children's world — parent-guided, no independent voice).
 */
import React from "react";
import { Link } from "react-router-dom";
import { Star, Moon, Sparkles, Footprints, BookHeart, Leaf } from "lucide-react";
import IntakeQuestion from "../components/IntakeQuestion";

const PAINTING_SRC = "/style_samples/room_polarstar_sample_v1.png";
const HOST_PORTRAIT_SRC = "/style_samples/room_polarstar_sample_v1.png";
const PAINTING_USED = HOST_PORTRAIT_SRC;

const PAPER = "#F2E9D2";
const INK = "#3D2E1F";
const INK_DEEP = "#241809";
const INK_SOFT = "#5A4530";
const INK_MUTE = "#8A7560";
const SAGE_DEEP = "#5A6E48";
const RULE = "#C9B999";

const SOFT_MASK =
  "radial-gradient(ellipse 88% 92% at 50% 50%, rgba(0,0,0,1) 58%, rgba(0,0,0,0) 100%)";

const OFFERINGS = [
  {
    icon: Star,
    title: "Look up",
    line: "There is always one light still on.",
  },
  {
    icon: Moon,
    title: "Be small for a moment",
    line: "Not everything has to be solved tonight.",
  },
  {
    icon: Sparkles,
    title: "Hear a quiet story",
    line: "Made for children, not for selling.",
  },
  {
    icon: Footprints,
    title: "Walk gently to sleep",
    line: "Your day is allowed to end.",
  },
  {
    icon: BookHeart,
    title: "Read together",
    line: "The smallest ritual is enough.",
  },
];

function Flourish({ color = RULE }) {
  return (
    <div
      className="flex items-center justify-center gap-3 my-4"
      aria-hidden="true"
    >
      <span style={{ height: 1, width: 60, background: color, opacity: 0.55 }} />
      <Leaf size={14} style={{ color, opacity: 0.7 }} />
      <span style={{ height: 1, width: 60, background: color, opacity: 0.55 }} />
    </div>
  );
}

export default function PolarstarIntro() {
  return (
    <div
      data-testid="polarstar-intro-page"
      style={{
        backgroundColor: PAPER,
        color: INK,
        minHeight: "100vh",
        backgroundImage:
          "radial-gradient(ellipse at center, rgba(255,255,255,0.25) 0%, rgba(0,0,0,0) 70%)",
      }}
    >
      <div
        className="mx-auto grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-8 lg:gap-16
                   px-6 sm:px-10 lg:px-16 py-10 lg:py-16"
        style={{ maxWidth: 1400 }}
      >
        {/* ============== LEFT — VERTICAL PAINTING ============== */}
        <div className="relative" data-testid="polarstar-intro-painting-wrapper">
          <div
            className="relative overflow-hidden mx-auto"
            style={{ aspectRatio: "3 / 4", maxWidth: 560, width: "100%" }}
          >
            <img
              src={PAINTING_USED}
              alt="Painting of the Polarstar's world — a soft night sky above a child's window, a single bright star holding steady overhead. The image carries the feeling of safety, quiet wonder and a light that does not leave."
              draggable={false}
              loading="eager"
              data-testid="polarstar-intro-painting"
              className="absolute inset-0 w-full h-full select-none"
              style={{
                objectFit: "cover",
                objectPosition: "center 45%",
                WebkitMaskImage: SOFT_MASK,
                maskImage: SOFT_MASK,
                WebkitMaskRepeat: "no-repeat",
                maskRepeat: "no-repeat",
              }}
            />
          </div>
        </div>

        {/* ============== RIGHT — TEXT PANEL ============== */}
        <div
          className="relative flex flex-col"
          style={{ paddingTop: "1.5rem" }}
          data-testid="polarstar-intro-panel"
        >
          <div
            aria-hidden="true"
            className="hidden lg:block absolute left-0 top-8 bottom-8"
            style={{ width: 1, background: RULE, opacity: 0.35 }}
          />
          <div
            aria-hidden="true"
            className="hidden lg:block absolute right-0 top-8 bottom-8"
            style={{ width: 1, background: RULE, opacity: 0.35 }}
          />

          <div className="lg:px-8">
            <div
              className="text-center text-[0.7rem] tracking-[0.3em] uppercase mb-4"
              style={{ color: INK_MUTE }}
              data-testid="polarstar-intro-eyebrow"
            >
              Keeper of wonder
            </div>

            <h1
              className="aurin-serif text-center tracking-tight"
              style={{
                color: INK_DEEP,
                fontSize: "clamp(3rem, 6vw, 5rem)",
                lineHeight: 1,
                margin: 0,
              }}
              data-testid="polarstar-intro-name"
            >
              Polarstar
            </h1>

            <Flourish />

            <p
              className="aurin-serif-italic text-center"
              style={{
                color: INK_SOFT,
                fontSize: "clamp(1.2rem, 1.7vw, 1.5rem)",
                lineHeight: 1.4,
                marginTop: "0.75rem",
              }}
              data-testid="polarstar-intro-greeting"
            >
              Hello. I am the Polarstar.
            </p>

            <p
              className="text-center mt-4"
              style={{
                color: INK,
                fontSize: "clamp(0.98rem, 1.1vw, 1.1rem)",
                lineHeight: 1.7,
                maxWidth: 480,
                marginLeft: "auto",
                marginRight: "auto",
              }}
              data-testid="polarstar-intro-body"
            >
              I live in the sky above your bedtime — a small light that does not leave. I am here for the children, for the parents who read to them, and for anyone who still remembers how it felt to be small and looking up.
            </p>

            <div
              className="flex items-center justify-center gap-3 mt-10 mb-6"
              aria-hidden="false"
            >
              <span style={{ height: 1, width: 36, background: RULE, opacity: 0.6 }} />
              <span
                className="text-[0.72rem] tracking-[0.3em] uppercase"
                style={{ color: SAGE_DEEP }}
                data-testid="polarstar-intro-offerings-heading"
              >
                Here, you may
              </span>
              <span style={{ height: 1, width: 36, background: RULE, opacity: 0.6 }} />
            </div>

            <ul
              className="space-y-5 max-w-md mx-auto"
              data-testid="polarstar-intro-offerings"
            >
              {OFFERINGS.map(({ icon: Icon, title, line }, i) => (
                <li
                  key={title}
                  className="flex items-start gap-4"
                  data-testid={`polarstar-intro-offering-${i + 1}`}
                >
                  <Icon
                    size={26}
                    strokeWidth={1.5}
                    style={{ color: SAGE_DEEP, flexShrink: 0, marginTop: 2 }}
                    aria-hidden="true"
                  />
                  <div>
                    <div
                      className="aurin-serif"
                      style={{
                        color: INK_DEEP,
                        fontSize: "1.15rem",
                        lineHeight: 1.2,
                        marginBottom: 2,
                      }}
                    >
                      {title}
                    </div>
                    <div
                      style={{
                        color: INK_SOFT,
                        fontSize: "0.92rem",
                        lineHeight: 1.5,
                      }}
                    >
                      {line}
                    </div>
                  </div>
                </li>
              ))}
            </ul>

            <Flourish />

            <p
              className="aurin-serif-italic text-center mt-2"
              style={{
                color: INK_SOFT,
                fontSize: "clamp(1.05rem, 1.4vw, 1.25rem)",
                lineHeight: 1.55,
              }}
              data-testid="polarstar-intro-closing-line"
            >
              Take what you need. Leave what does not fit.
              <br />
              The star will be here when you return.
            </p>

            <div className="flex justify-center mt-8">
              <Link
                to="/kids-universe/polarstar"
                data-testid="polarstar-intro-enter-button"
                className="inline-block transition-colors duration-300"
                style={{
                  backgroundColor: SAGE_DEEP,
                  color: PAPER,
                  borderRadius: 9999,
                  padding: "0.95rem 2.4rem",
                  fontSize: "0.78rem",
                  letterSpacing: "0.3em",
                  textTransform: "uppercase",
                  boxShadow: "0 6px 16px -8px rgba(0,0,0,0.35)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = INK_DEEP;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = SAGE_DEEP;
                }}
              >
                Enter Polarstar Kids
              </Link>
            </div>

            <div
              className="flex items-center justify-center gap-3 mt-10"
              aria-hidden="false"
            >
              <span style={{ height: 1, width: 70, background: RULE, opacity: 0.5 }} />
              <span
                className="aurin-serif-italic"
                style={{
                  color: INK_SOFT,
                  fontSize: "1.4rem",
                  letterSpacing: "0.02em",
                }}
                data-testid="polarstar-intro-signature"
              >
                Polarstar
              </span>
              <span style={{ height: 1, width: 70, background: RULE, opacity: 0.5 }} />
            </div>
          </div>
        </div>
      </div>
      <IntakeQuestion path="/polarstar/intro" />
    </div>
  );
}
